/**
 * The desktop app bridge (apps/desktop). Present only inside the vidmakr
 * desktop app; every desktop feature must check for it.
 */
import { type DesktopBridge, type DesktopDirEntry } from '@vidmakr/shared/desktop';
import type { UploadSource } from './upload';

declare global {
  interface Window {
    vidmakrDesktop?: DesktopBridge;
  }
}

export const desktop: DesktopBridge | undefined = typeof window !== 'undefined' ? window.vidmakrDesktop : undefined;

/**
 * Where the "Download the desktop app" links go: this server's /download
 * page, which serves the installers from the (private) GitHub releases.
 * VITE_DESKTOP_DOWNLOAD_URL points them elsewhere.
 */
export const DESKTOP_DOWNLOAD_URL: string = import.meta.env.VITE_DESKTOP_DOWNLOAD_URL || '/download';

/**
 * A file on a local or external drive as an upload source: the desktop app
 * reads each upload part from disk when it is sent, so multi-gigabyte camera
 * files never sit in memory as a whole.
 */
export function localFileSource(bridge: DesktopBridge, entry: DesktopDirEntry): UploadSource {
  return {
    name: entry.name,
    size: entry.sizeBytes,
    type: '',
    async slice(start, end) {
      const bytes = await bridge.readFileRange(entry.path, start, Math.min(end, entry.sizeBytes));
      if (bytes.byteLength !== Math.min(end, entry.sizeBytes) - start) throw new Error(`${entry.name} changed or was removed while uploading`);
      return new Blob([bytes as Uint8Array<ArrayBuffer>]);
    },
  };
}
